// Dependencies
import React, {
  useState,
  useImperativeHandle,
  useRef,
  forwardRef,
  useCallback,
  useLayoutEffect,
  Ref,
  CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';

// Types
import { DropDownProps, ImperativeRefList, OptionSelected } from './types';

// Components Styled
import { DropDownListContainer, List, ListItem } from './styles';

const DropDownList = (
  {
    defaultVisible,
    options,
    typeDropDown,
    selectedOption,
    setSelectedAndCallback,
    placement,
    portal,
    anchorRef,
    ...props
  }: DropDownProps,
  innerRef: Ref<ImperativeRefList>
): JSX.Element => {
  const listRef = useRef<HTMLDivElement>(null);
  const ulRef = useRef<HTMLUListElement>(null);

  const [visible, setVisible] = useState<boolean>(defaultVisible ?? false);
  const [portalStyle, setPortalStyle] = useState<CSSProperties | undefined>(
    undefined
  );

  const clickOption = useCallback((option: OptionSelected) => {
    setSelectedAndCallback(option);
    setVisible(false);
    // Bug fix: deps `[]` capturaba `setSelectedAndCallback` del primer render
    // → stale closure. Mismo patrón roto que `onChangeList` en DropDownInput.
  }, [setSelectedAndCallback]);

  const setVisibleOut = useCallback(() => setVisible(!visible), [visible]);

  useImperativeHandle(innerRef, () => ({
    setVisibleDropDown: setVisibleOut,
    setHidden: () => setVisible(false),
    getListEl: () => ulRef.current,
  }));

  // Modo portal: el panel se renderiza en document.body con position:fixed
  // anclado al trigger, para escapar de contenedores con overflow (p.ej. la
  // tabla de animales seleccionados con scroll) sin recortarse. Se recalcula
  // en scroll/resize mientras está abierto para seguir pegado al trigger.
  useLayoutEffect(() => {
    if (!portal || !visible) return;
    const compute = () => {
      const anchor = anchorRef?.current;
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      const gap = 4;
      const listH = ulRef.current?.offsetHeight ?? 0;
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp =
        listH > 0 && spaceBelow < listH + gap && r.top > spaceBelow;
      const top = openUp ? Math.max(gap, r.top - listH - gap) : r.bottom + gap;
      setPortalStyle({
        position: 'fixed',
        top,
        left: r.left,
        minWidth: r.width,
        zIndex: 9999,
      });
    };
    compute();
    // Segunda pasada tras montar la lista para medir su altura real y decidir
    // si abre hacia arriba (trigger cerca del borde inferior del viewport).
    const raf = requestAnimationFrame(compute);
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [portal, visible, anchorRef]);

  const listNode = visible ? (
    <List
      ref={ulRef}
      role={'select'}
      typeDropDown={typeDropDown}
      placement={portal ? undefined : placement}
      style={portal ? portalStyle : undefined}
    >
      {options.map((option, index) => (
        <ListItem
          onBlur={() => index === options.length - 1 && setVisible(false)}
          tabIndex={0}
          type={typeDropDown}
          value={option?.value}
          role={'option'}
          isActive={selectedOption?.value === option.value}
          onClick={() => {
            clickOption(option);
          }}
          key={index}
          onKeyDown={(event) => {
            if (event.key === ' ') {
              event.preventDefault();
              clickOption(option);
            }
          }}
        >
          {option.label}
        </ListItem>
      ))}
    </List>
  ) : null;

  return (
    <DropDownListContainer ref={listRef} {...props}>
      {portal
        ? listNode
          ? createPortal(listNode, document.body)
          : null
        : listNode}
    </DropDownListContainer>
  );
};

export default forwardRef(DropDownList);
